import { useState, useEffect, useCallback, useDeferredValue } from 'react';
import { useAppSelector } from './store';
import { AppShell } from './features/AppShell';
import { LandingPage } from './features/LandingPage';
import { PracticeScreen } from './features/PracticeScreen';
import { InlineScriptEditor } from './features/InlineScriptEditor';
import { OptionsMenu } from './features/OptionsMenu';
import { ReadthroughView } from './features/ReadthroughView';
import { RecordView } from './features/RecordView';

export type ViewName = 'upload' | 'character' | 'practice' | 'readthrough' | 'record' | 'edit';

export function App() {
    const [currentView, setCurrentView] = useState<ViewName>('upload');
    const parsedLines = useAppSelector(s => s.script.parsedLines);

    const canNavigateToPractice = parsedLines.length > 0;
    const deferredView = useDeferredValue(currentView);

    const navigate = useCallback((view: ViewName) => {
        setCurrentView(view);
    }, []);

    // Handle hash-based routing for edit-script links
    useEffect(() => {
        function handleHash() {
            const hash = window.location.hash || '';
            if (hash.startsWith('#/edit-script')) {
                setCurrentView('edit');
            }
        }
        handleHash();
        window.addEventListener('hashchange', handleHash);
        return () => window.removeEventListener('hashchange', handleHash);
    }, []);

    return (
        <div className="app-container">
            <AppShell
                currentView={currentView}
                onNavigate={navigate}
                canNavigateToPractice={canNavigateToPractice}
            />
            <div className="content-row">
                <div className="main-content">
                    {deferredView === 'upload' && <LandingPage onNavigate={navigate} />}
                    {deferredView === 'character' && <LandingPage onNavigate={navigate} showCharacterSelect />}
                    {deferredView === 'practice' && <PracticeScreen onNavigate={navigate} />}
                    {deferredView === 'readthrough' && <ReadthroughView onNavigate={navigate} />}
                    {deferredView === 'record' && <RecordView onNavigate={navigate} />}
                    {deferredView === 'edit' && <InlineScriptEditor onNavigate={navigate} />}
                </div>
                <OptionsMenu onNavigate={navigate} visible={deferredView === 'practice'} />
            </div>
        </div>
    );
}
